'use client';

import type { SearchProductResult } from '@chemist4u/newt-components';
import { COUNTRY_CODE } from '@lib/constants';
import { SearchProvider } from '@lib/context/search-context';
import type { SearchProductResult as BackendSearchProductResult } from '@lib/data/products';
import { searchProducts } from '@lib/data/products';
import { useDebounce } from '@lib/hooks/use-debounce';
import { sanitizeSearchQuery } from '@lib/util/sanitize-query';
import { useEffect, useState } from 'react';

/**
 * Maps backend SearchProductResult (calculated_price) to component library format (calculatedPrice)
 */
const mapBackendSearchResult = (
  result: BackendSearchProductResult,
): SearchProductResult => ({
  id: result.id,
  title: result.title,
  handle: result.handle,
  thumbnail: result.thumbnail,
  calculatedPrice: result.calculated_price,
});

type SearchProviderWrapperProps = {
  children?: React.ReactNode;
};

export const SearchProviderWrapper = ({
  children,
}: SearchProviderWrapperProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchProductResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const sanitizedQuery = sanitizeSearchQuery(query) ?? '';
  const sanitizedDebouncedQuery = sanitizeSearchQuery(debouncedQuery) ?? '';

  // Flip on as soon as a search-worthy query is typed, ahead of the debounced
  // fetch below — otherwise the dropdown's "no products found" empty state
  // flashes during the debounce/fetch window, before results have arrived.
  useEffect(() => {
    setIsLoading(sanitizedQuery.length >= 2);
  }, [sanitizedQuery]);

  // Fetch search results when debounced query changes
  useEffect(() => {
    if (sanitizedDebouncedQuery.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    searchProducts({
      query: sanitizedDebouncedQuery,
      countryCode: COUNTRY_CODE,
      limit: 6,
    })
      .then((data) => {
        if (!cancelled) {
          setResults(data.products.map(mapBackendSearchResult));
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sanitizedDebouncedQuery]);

  return (
    <SearchProvider
      query={query}
      results={results}
      setQuery={setQuery}
      isLoading={isLoading}
    >
      {children}
    </SearchProvider>
  );
};
